<template>
  <v-card>
    <v-toolbar color="indigo" elevation="4">
      <v-toolbar-title class="toolbar-title">nCode Metrics</v-toolbar-title>
      <v-spacer></v-spacer>

      <template v-slot:extension>
        <v-tabs v-model="tab" align-tabs="title">
          <v-tab v-for="item in tabItems" :key="item" :value="item">
            {{ item }}
          </v-tab>
        </v-tabs>
      </template>
    </v-toolbar>

    <div v-if="tab === 'overview'" class="tiles-container">
      <!-- Code Completion Acceptance Rate Card -->
      <v-card elevation="4" color="white" variant="elevated" class="mx-auto my-3" style="width: 300px; height: 175px;">
        <v-card-item>
          <div class="tiles-text">
            <div class="spacing-10"></div>
            <div class="text-h6 mb-1">Code Acceptance Rate</div>
            <div class="text-caption">Code accepted</div>
            <p class="text-h4">
              {{ ((metrics.breakdown.completion.totalAccepted / metrics.breakdown.completion.totalSuggested) * 100).toFixed(2) }}%
            </p>
          </div>
        </v-card-item>
      </v-card>

      <!-- Code Completion Card -->
      <v-card elevation="4" color="white" variant="elevated" class="mx-auto my-3" style="width: 300px; height: 175px;">
        <v-card-item>
          <div class="tiles-text">
            <div class="spacing-10"></div>
            <div class="text-h6 mb-1">Lines of Code Accepted</div>
            <div class="text-caption">Code Completion</div>
            <p class="text-h4">{{ metrics.breakdown.completion.totalAccepted }}</p>
          </div>
        </v-card-item>
      </v-card>

      <!-- Code Documentation Card -->
      <v-card elevation="4" color="white" variant="elevated" class="mx-auto my-3" style="width: 300px; height: 175px;">
        <v-card-item>
          <div class="tiles-text">
            <div class="spacing-10"></div>
            <div class="text-h6 mb-1">Documentation Requests</div>
            <div class="text-caption">Total Requests</div>
            <p class="text-h4">{{ metrics.breakdown.documentation.totalRequests }}</p>
          </div>
        </v-card-item>
      </v-card>

      <!-- Code Explanation Card -->
      <v-card elevation="4" color="white" variant="elevated" class="mx-auto my-3" style="width: 300px; height: 175px;">
        <v-card-item>
          <div class="tiles-text">
            <div class="spacing-10"></div>
            <div class="text-h6 mb-1">Explanation Requests</div>
            <div class="text-caption">Total Requests</div>
            <p class="text-h4">{{ metrics.breakdown.explanation.totalRequests }}</p>
          </div>
        </v-card-item>
      </v-card>

      <!-- Test Cases Card -->
      <v-card elevation="4" color="white" variant="elevated" class="mx-auto my-3" style="width: 300px; height: 175px;">
        <v-card-item>
          <div class="tiles-text">
            <div class="spacing-10"></div>
            <div class="text-h6 mb-1">Test Cases Generated</div>
            <div class="text-caption">Total Lines Suggested</div>
            <p class="text-h4">{{ metrics.breakdown.testcase.totalSuggested }}</p>
          </div>
        </v-card-item>
      </v-card>

      <!-- Pie Chart for Overview -->
      <div class="chart-container">
        <v-card elevation="4" color="white" variant="elevated" class="mx-auto my-3" style="width: 500px;">
          <v-card-item>
            <div class="text-h6 mb-4 text-center">Code Distribution</div>
            <Pie
              :data="chartData"
              :options="chartOptions"
            />
          </v-card-item>
        </v-card>
      </div>
    </div>

    <div v-if="tab === 'users'" class="users-container">
      <h3>Total Users: {{ users.length }}</h3>
      <v-list>
        <v-list-item v-for="user in users" :key="user.email" class="user-list-item">
          <v-list-item-title class="user-email">{{ user.email }}</v-list-item-title>
          <v-list-item-subtitle class="user-projectid">Project ID: {{ user.projectid }}</v-list-item-subtitle>
        </v-list-item>
      </v-list>
    </div>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, watch } from 'vue'
import { fetchNCodeMetrics, fetchAllUsers } from '@/api/nCodeApi'
import type { NCodeMetrics } from '@/api/nCodeApi'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'
import { Pie } from 'vue-chartjs'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { VCard, VToolbar, VToolbarTitle, VSpacer, VTabs, VTab, VList, VListItem, VListItemTitle, VListItemSubtitle } from 'vuetify/components'

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels)

export default defineComponent({
  name: 'NCodeMetrics',
  components: {
    Pie,
    VCard,
    VToolbar,
    VToolbarTitle,
    VSpacer,
    VTabs,
    VTab,
    VList,
    VListItem,
    VListItemTitle,
    VListItemSubtitle
  },
  setup() {
    const tab = ref('overview');
    const tabItems = ref(['overview', 'users']);
    const metrics = ref<NCodeMetrics>({
      totalRequests: 0,
      totalSuggested: 0,
      totalAccepted: 0,
      breakdown: {
        completion: {
          totalRequests: 0,
          totalSuggested: 0,
          totalAccepted: 0
        },
        explanation: {
          totalRequests: 0,
        },
        documentation: {
          totalRequests: 0,
        },
        testcase: {
          totalRequests: 0,
          totalSuggested: 0
        },
      }
    });

    const users = ref<any[]>([]);

    const chartData = computed(() => ({
      labels: ['Lines of Code Accepted', 'Test Cases Generated'],
      datasets: [{
        data: [
          metrics.value.breakdown.completion.totalAccepted,
          metrics.value.breakdown.testcase.totalSuggested
        ],
        backgroundColor: [
          'rgba(103, 58, 183, 0.8)',   // Deep Purple
          'rgba(156, 39, 176, 0.8)'    // Purple
        ],
        borderColor: [
          'rgba(103, 58, 183, 1)',
          'rgba(156, 39, 176, 1)'
        ],
        borderWidth: 2
      }]
    }));

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          position: 'bottom' as const,
          labels: {
            font: {
              size: 14
            }
          }
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const value = context.raw;
              const percentage = ((value / context.dataset.data.reduce((a: number, b: number) => a + b, 0)) * 100).toFixed(1);
              return `${context.label}: ${value} (${percentage}%)`;
            }
          }
        },
        datalabels: {
          color: '#fff',
          font: {
            weight: 'bold',
            size: 14
          },
          formatter: (value: number, context: any) => {
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${value}\n(${percentage}%)`;
          },
          anchor: 'center',
          align: 'center',
          offset: 0
        }
      }
    };

    onMounted(async () => {
      metrics.value = await fetchNCodeMetrics();
    });

    const fetchUsers = async () => {
      try {
        const response = await fetchAllUsers();
        console.log("Fetched users:", response);
        users.value = response;
      } catch (error) {
        console.error("Failed to fetch users:", error);
      }
    };

    watch(tab, (newTab) => {
      if (newTab === 'users') {
        fetchUsers();
      }
    });

    return {
      tab,
      tabItems,
      metrics,
      users,
      chartData,
      chartOptions
    }
  }
})
</script>

<style scoped>
.toolbar-title {
  white-space: nowrap;
  overflow: visible;
  text-overflow: clip;
}

.tiles-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  padding: 20px;
}

.users-container {
  padding: 20px;
}

.user-list-item {
  background-color: #ffffff; /* Match the app's background color */
  margin-bottom: 10px;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); /* Subtle shadow for depth */
  transition: background-color 0.3s ease;
}

.user-list-item:hover {
  background-color: #f5f5f5; /* Slightly darker on hover */
}

.user-email {
  color: #3f51b5; /* Indigo color for email */
  font-weight: bold;
}

.user-projectid {
  color: #ff5722; /* Deep orange color for project ID */
  font-size: 0.9em;
}

.tiles-text {
  text-align: center;
}

.spacing-10 {
  height: 10px;
}

/* Remove the old styles that are no longer needed */
.metrics-grid,
.metric-card,
.metric-header,
.metric-content,
.metric-item,
.metric-label,
.metric-value {
  display: none;
}

.chart-container {
  display: flex;
  justify-content: center;
  padding: 20px;
  margin-bottom: 20px;
}
</style> 